import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TableScroll } from '../../components/TableScroll'
import type { TableColumn } from '../../lib/tableView'
import { useTableView } from '../../lib/useTableView'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { apiErrorMessage } from '../../lib/errors'
import { formatDateTime } from '../../lib/format'
import {
  downloadBroadcastMessage,
  getBroadcast,
  isInFlight,
  type BroadcastDetail,
  type BroadcastRecipient,
} from './api'
import { countsText, modeText, statusText, statusTone } from './describe'
import type { DeliveryStatus } from './api'
import { MessagePreview } from './MessagePreview'

/** How often the page asks again while anything is still queued — the drain works in seconds. */
export const REFRESH_MS = 3_000

/**
 * One broadcast: the message it sent, and what happened to each recipient's copy — read
 * again every few seconds until nothing is queued. In Recording nothing left the platform,
 * and the page says so; every composed message downloads as an .eml, which opens in Mail or
 * Outlook as if it had arrived (Lino, 2026-09-30: Hugo reads them without anyone touching
 * the server).
 */
export function BroadcastDetailPage() {
  const { t, i18n } = useTranslation()
  const { id: postingId, broadcastId } = useParams()

  const [broadcast, setBroadcast] = useState<BroadcastDetail | null>(null)
  // The recipients sort and filter by their columns (TRKC-110); a re-read while the drain
  // works keeps the view.
  const columns = useMemo(() => recipientColumns(t), [t])
  const view = useTableView(broadcast?.recipients ?? NO_RECIPIENTS, columns)
  const statusOptions = useMemo(
    () => DELIVERY_STATUSES.map((status) => ({ value: status, label: t(`broadcasts.statuses.${status}`) })),
    [t],
  )
  const [failure, setFailure] = useState<{ error: unknown } | null>(null)
  const [downloadFailure, setDownloadFailure] = useState<{ error: unknown } | null>(null)
  const [reading, setReading] = useState(0)
  // Every reading that finished, failed ones included — what the next one waits on.
  const [settled, setSettled] = useState(0)

  useEffect(() => {
    if (broadcastId === undefined) {
      return
    }

    let active = true

    getBroadcast(broadcastId)
      .then((found) => {
        if (active) {
          setBroadcast(found)
          setFailure(null)
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setFailure({ error: caught })
        }
      })
      .finally(() => {
        if (active) {
          setSettled((current) => current + 1)
        }
      })

    return () => {
      active = false
    }
  }, [broadcastId, reading])

  // While the drain still has copies to work through, ask again after every reading — a
  // failed one included, or a refresh that fails once would stop the page asking for good
  // (Copilot on #44) — and stop the moment nothing is queued, so a finished broadcast costs
  // nothing to keep open.
  const inFlight = broadcast !== null && isInFlight(broadcast.counts)
  useEffect(() => {
    if (!inFlight) {
      return
    }

    const timer = window.setTimeout(() => setReading((current) => current + 1), REFRESH_MS)
    return () => window.clearTimeout(timer)
  }, [inFlight, settled])

  if (broadcast === null) {
    return failure === null ? (
      <p className="session-check">{t('broadcasts.loading')}</p>
    ) : (
      <section className="page">
        <Breadcrumbs
          trail={[
            { label: t('nav.availability'), to: '/availability' },
            { label: null, to: `/availability/${postingId}` },
            { label: null },
          ]}
        />
        <p className="alert" role="alert">
          {apiErrorMessage(failure.error, t, 'broadcasts.loadError')}
        </p>
      </section>
    )
  }

  const language = i18n.language
  const narrowed = broadcast.environmentMode !== broadcast.mode

  async function download(recipient: BroadcastRecipient) {
    setDownloadFailure(null)

    try {
      await downloadBroadcastMessage(broadcast!, recipient)
    } catch (caught) {
      setDownloadFailure({ error: caught })
    }
  }

  return (
    <section className="page">
      <Breadcrumbs
        trail={[
          { label: t('nav.availability'), to: '/availability' },
          { label: broadcast.reference, to: `/availability/${postingId ?? broadcast.postingId}` },
          { label: t('breadcrumbs.broadcastOn', { when: formatDateTime(broadcast.createdAt, language) }) },
        ]}
      />
      <header className="page__header page__header--row">
        <div>
          <h1>{t('broadcasts.detailTitle', { reference: broadcast.reference })}</h1>
          <p className="page__subtitle">
            {[
              formatDateTime(broadcast.createdAt, language),
              broadcast.sentByEmail === null ? null : t('broadcasts.sentBy', { email: broadcast.sentByEmail }),
              countsText(broadcast.counts, t),
            ]
              .filter((part) => part !== null && part !== '')
              .join(' · ')}
          </p>
        </div>
        <Link className="button button--quiet" to={`/availability/${postingId ?? broadcast.postingId}`}>
          {t('broadcasts.backToPosting')}
        </Link>
      </header>

      <p className="broadcast-mode" role="status">
        <strong>{modeText(broadcast.mode, t)}</strong> {t(`broadcasts.sentNotes.${broadcast.mode}`)}
      </p>

      {narrowed && inFlight && (
        <p className="alert alert--warning" role="status">
          {t('broadcasts.narrowed', { mode: modeText(broadcast.environmentMode, t) })}
        </p>
      )}

      {downloadFailure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(downloadFailure.error, t, 'broadcasts.downloadError')}
        </p>
      )}

      <section className="broadcast-section">
        <h2>{t('broadcasts.recipients.delivery')}</h2>
        {inFlight && (
          <p className="muted" role="status">
            {t(failure === null ? 'broadcasts.stillQueued' : 'broadcasts.refreshFailed')}
          </p>
        )}
        <TableScroll label={t('broadcasts.recipients.delivery')}>
          <table className="table">
            <caption className="visually-hidden">{t('broadcasts.recipients.delivery')}</caption>
            <thead>
              <tr>
                {columns.map((column) => (
                  <SortableHeader key={column.key} view={view} column={column.key}>
                    {t(RECIPIENT_HEADINGS[column.key] ?? column.key)}
                  </SortableHeader>
                ))}
              </tr>
              <tr className="table__filters">
                {columns.map((column) => (
                  <FilterCell
                    key={column.key}
                    view={view}
                    column={column}
                    label={t(RECIPIENT_HEADINGS[column.key] ?? column.key)}
                    options={column.key === 'status' ? statusOptions : undefined}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {view.rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="placeholder">
                    {t('table.noneMatch')}
                  </td>
                </tr>
              )}
              {view.rows.map((recipient) => (
                <tr key={recipient.id}>
                  <td>
                    {recipient.agentName}
                    <br />
                    <span className="muted">{recipient.brokerName}</span>
                  </td>
                  <td className="broadcast-recipients__address">{recipient.address}</td>
                  <td>
                    <span className={`badge badge--${statusTone(recipient)}`}>{statusText(recipient, t)}</span>
                    {recipient.processedAt !== null && (
                      <>
                        <br />
                        <span className="muted">{formatDateTime(recipient.processedAt, language)}</span>
                      </>
                    )}
                    {recipient.failureDetail !== null && (
                      <>
                        <br />
                        <span className="muted">{recipient.failureDetail}</span>
                      </>
                    )}
                  </td>
                  <td>
                    {recipient.hasMessage ? (
                      <button
                        type="button"
                        className="button button--quiet"
                        aria-label={t('broadcasts.downloadEmlFor', { name: recipient.agentName })}
                        onClick={() => void download(recipient)}
                      >
                        {t('broadcasts.downloadEml')}
                      </button>
                    ) : (
                      <span className="muted">{t('broadcasts.noMessage')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        <FilterSummary view={view} />
      </section>

      <MessagePreview message={broadcast.message} />
    </section>
  )
}

/** Every status a copy can be in, in the order the status column sorts them. */
const DELIVERY_STATUSES: readonly DeliveryStatus[] = ['Queued', 'Sending', 'Recorded', 'Sent', 'Suppressed', 'Failed']

/** What each column of the recipients sorts and filters by (TRKC-110). */
function recipientColumns(t: TFunction): TableColumn<BroadcastRecipient>[] {
  return [
    {
      key: 'agent',
      sortValue: (recipient) => recipient.agentName,
      filter: { kind: 'text', text: (recipient) => `${recipient.agentName} ${recipient.brokerName}` },
    },
    { key: 'address', sortValue: (recipient) => recipient.address, filter: { kind: 'text', text: (recipient) => recipient.address } },
    {
      key: 'status',
      sortValue: (recipient) => statusText(recipient, t),
      filter: { kind: 'choice', values: DELIVERY_STATUSES, matches: (recipient, status) => recipient.status === status },
    },
    { key: 'message', sortValue: (recipient) => (recipient.hasMessage ? 0 : 1) },
  ]
}

/** The heading each column's filter box is named after. */
const RECIPIENT_HEADINGS: Record<string, string> = {
  agent: 'broadcasts.columns.agent',
  address: 'broadcasts.columns.address',
  status: 'broadcasts.columns.status',
  message: 'broadcasts.columns.message',
}

/** No broadcast yet: one array, so the view is not recomputed for an empty one every render. */
const NO_RECIPIENTS: BroadcastRecipient[] = []
